// frontend/src/App.tsx
// Версия: 3.2.0 (2026-01-13)
//
// ИЗМЕНЕНИЯ v3.2.0:
// - ДОБАВЛЕНО: Динамический basename из config/paths.ts
// - ИСПРАВЛЕНО: Хардкод basename="/ssas" → basename={BASE_PATH}
//
// ИЗМЕНЕНИЯ v3.1.0:
// - ДОБАВЛЕНО: Блок /admin/* роутов для роли admin
// - ДОБАВЛЕНО: AdminTestingSettings импорт и роуты
// - ДОБАВЛЕНО: Заглушка AdminUsersStub для /admin/users
// - ИСПРАВЛЕНО: sessionStorage → localStorage (согласно AuthContext)
// - ИСПРАВЛЕНО: Редирект /admin/settings/testing работает
//
// ДОБАВЛЕНО (Variant B):
// - TrainingModeProvider вынесен СНАРУЖИ AuthProvider (как в ТЗ),
//   чтобы training mode был глобален для всего приложения.

import React from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  Outlet,
} from 'react-router-dom';

import QuickRequestEntry from './pages/QuickRequestEntry';
import MainAuthPage from './pages/MainAuthPage';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { TrainingModeProvider } from './contexts/TrainingModeContext';

import RoleBasedLayout from './layouts/RoleBasedLayout';

// Страницы
import OperatorDashboard from './pages/OperatorDashboard';
import ClientPortal from './pages/ClientPortal';
import ClientRequests from './pages/ClientRequests';
import ClientReports from './pages/ClientReports';
import ClientRequestForm from './pages/ClientRequestForm';
import ClientUserGuide from './pages/ClientUserGuide';
import DutyShiftReport from './pages/DutyShiftReport';
import LoginPage from './pages/LoginPage';
import UnauthorizedPage from './pages/UnauthorizedPage';

// ДОБАВЛЕНО v3.1.0: Импорт страницы настроек админа
import AdminTestingSettings from './pages/admin/AdminTestingSettings';

// HRA Piracy Monitoring (admin-only)
import AdminPiracyDashboard from './pages/admin/AdminPiracyDashboard';

// ShipOwner Reports (operator + admin)
import ShipOwnerReportPage from './pages/ShipOwnerReportPage';

// Компоненты
import Dashboard from './components/dashboard.component';
import RequestForm from './components/RequestForm';
import TestingForm from './components/testingForm.component';
import RequestList from './components/RequestList';
import SignalMonitor from './components/SignalMonitor';
import ReportsPage from './components/ReportsPage';
import MapView from './components/MapView';
import { BASE_PATH } from './config/paths';
import './styles/print.css';

type Role = 'operator' | 'client' | 'admin';

// ДОБАВЛЕНО v3.1.0: Заглушка для страницы управления пользователями
function AdminUsersStub() {
  return (
    <div style={{ padding: '2rem', textAlign: 'center' }}>
      <h2>Управление пользователями</h2>
      <p style={{ color: '#666' }}>Раздел находится в разработке</p>
    </div>
  );
}

function ProtectedRoute({ allowedRoles }: { allowedRoles: Role[] }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;

  // ИСПРАВЛЕНО v3.1.0: localStorage вместо sessionStorage (согласно AuthContext)
  let effectiveUser = user;
  if (!effectiveUser) {
    const token = localStorage.getItem('token');
    const role = localStorage.getItem('role') as Role | null;
    const email = localStorage.getItem('email');
    if (token && role) {
      effectiveUser = {
        email: email ?? '',
        role,
        id: '',
        organization_name: null,
        full_name: null,
      } as any;
    }
  }

  if (!effectiveUser) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (allowedRoles.length && !allowedRoles.includes(effectiveUser.role as Role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <Outlet />;
}

function NotFoundPage() {
  const { user } = useAuth();
  if (user?.role === 'client') return <Navigate to="/client" replace />;
  if (user?.role === 'admin') return <Navigate to="/admin" replace />;
  return <Navigate to="/operator" replace />;
}

// Обертка для future flags
function AppWithRouter() {
  return (
    <Router basename={BASE_PATH}>
      <Routes>
        {/* Публичные */}
        <Route path="/login" element={<MainAuthPage />} />
        <Route path="/login/operator" element={<LoginPage />} />
        <Route path="/login/admin" element={<LoginPage />} />
        <Route path="/login-legacy" element={<LoginPage />} />
        <Route path="/request-entry" element={<QuickRequestEntry />} />
        {/* ДОБАВЛЕНО v3.3.0: Запоминающийся URL для судовладельцев */}
        <Route path="/apply" element={<QuickRequestEntry />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />

        {/* ВАЖНО: Роуты обратной совместимости */}
        <Route path="/client-requests" element={<Navigate to="/client/requests" replace />} />
        <Route path="/client/new-request" element={<Navigate to="/client/request/new" replace />} />

        {/* ============================================================ */}
        {/* ДОБАВЛЕНО v3.1.0: Блок администратора (/admin/*) */}
        {/* Админ имеет доступ ко всем операторским функциям + настройки */}
        {/* ============================================================ */}
        <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
          <Route element={<RoleBasedLayout />}>
            {/* Главная панель админа (та же что у оператора) */}
            <Route path="/admin" element={<OperatorDashboard />} />

            {/* Операторские страницы под /admin namespace */}
            <Route path="/admin/requests" element={<RequestList />} />
            <Route path="/admin/request" element={<RequestForm />} />
            <Route path="/admin/signals" element={<SignalMonitor />} />
            <Route path="/admin/testing" element={<TestingForm />} />
            <Route path="/admin/reports" element={<ReportsPage />} />
            <Route path="/admin/dashboard" element={<Dashboard />} />
            <Route path="/admin/map" element={<MapView />} />

            {/* Админ-специфичные страницы */}
            <Route path="/admin/settings" element={<AdminTestingSettings />} />
            <Route path="/admin/settings/testing" element={<AdminTestingSettings />} />
            <Route path="/admin/users" element={<AdminUsersStub />} />
            <Route path="/admin/piracy" element={<AdminPiracyDashboard />} />
            <Route path="/admin/shipowner-reports" element={<ShipOwnerReportPage />} />
            <Route path="/admin/duty-report" element={<DutyShiftReport />} />
          </Route>
        </Route>

        {/* Блок оператора */}
        <Route element={<ProtectedRoute allowedRoles={['operator', 'admin']} />}>
          <Route element={<RoleBasedLayout />}>
            <Route path="/operator" element={<Navigate to="/operator/requests" replace />} />
            <Route path="/operator/requests" element={<RequestList />} />
            <Route path="/operator/request" element={<RequestForm />} />
            <Route path="/operator/signals" element={<SignalMonitor />} />
            <Route path="/operator/testing" element={<TestingForm />} />
            <Route path="/operator/reports" element={<ReportsPage />} />
            <Route
              path="/operator/dashboard"
              element={<Navigate to="/operator/requests" replace />}
            />
            <Route path="/operator/map" element={<MapView />} />

            <Route path="/operator/shipowner-reports" element={<ShipOwnerReportPage />} />
            <Route path="/operator/duty-report" element={<DutyShiftReport />} />
          </Route>
        </Route>

        {/* Блок клиента (и админа) */}
        <Route element={<ProtectedRoute allowedRoles={['client', 'admin']} />}>
          <Route element={<RoleBasedLayout />}>
            <Route path="/client" element={<ClientPortal />} />
            <Route path="/client/requests" element={<ClientRequests />} />
            <Route path="/client/reports" element={<ClientReports />} />

            {/* ВАЖНО: Роут для формы создания заявки клиента */}
            <Route path="/client/request/new" element={<ClientRequestForm />} />
            <Route path="/client/guide" element={<ClientUserGuide />} />
          </Route>
        </Route>

        <Route
          path="/shipowner-reports"
          element={<Navigate to="/operator/shipowner-reports" replace />}
        />

        {/* Роуты обратной совместимости со старыми путями */}
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/requests" element={<Navigate to="/operator/requests" replace />} />
        <Route path="/request" element={<Navigate to="/operator/request" replace />} />
        <Route path="/signals" element={<Navigate to="/operator/signals" replace />} />
        <Route path="/testing" element={<Navigate to="/operator/testing" replace />} />
        <Route path="/reports" element={<Navigate to="/operator/reports" replace />} />
        <Route path="/dashboard" element={<Navigate to="/operator/requests" replace />} />

        {/* Глобальный fallback */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <TrainingModeProvider>
      <AuthProvider>
        <AppWithRouter />
      </AuthProvider>
    </TrainingModeProvider>
  );
}

export default App;
